import { useMemo } from 'react';

import keyBy from 'lodash/keyBy';

import { ClusterDisplayStatus } from '@cast/types';
import { makeRefetchOnlyFailed } from '@cast/utils';

import { partitionNotFoundClusters } from 'components/cluster/not-found-clusters';

import { useCastClusterRowsModels } from './useCastClusterRowModels';
import { useDiscoveredClusterRowModels } from './useDiscoveredClusterRowModels';
import { ClusterRowModel, ClusterRowDisplayMetrics } from '../../types';

// Cloud Asset data carries used/requested; the external provisioner tags only
// carry allocatable totals — prefer discovered values, fall back to tags
const mergeResources = (
  castResources?: ClusterRowDisplayMetrics['resources'],
  discoveredResources?: ClusterRowDisplayMetrics['resources']
): ClusterRowDisplayMetrics['resources'] => {
  if (!castResources) return discoveredResources;
  if (!discoveredResources) return castResources;

  return {
    cpu: {
      allocatable:
        discoveredResources.cpu.allocatable || castResources.cpu.allocatable,
      used: discoveredResources.cpu.used || castResources.cpu.used,
      requested:
        discoveredResources.cpu.requested || castResources.cpu.requested,
    },
    memory: {
      allocatable:
        discoveredResources.memory.allocatable ||
        castResources.memory.allocatable,
      used: discoveredResources.memory.used || castResources.memory.used,
      requested:
        discoveredResources.memory.requested || castResources.memory.requested,
    },
    gpu: castResources.gpu,
  };
};

export const useClusterListRowModels = () => {
  const cast = useCastClusterRowsModels();
  const discovered = useDiscoveredClusterRowModels();

  // intentionally ignore cast loading to make data appear faster
  const isLoading = cast.isClustersLoading || discovered.isLoading;

  const clusterListRowModels = useMemo<ClusterRowModel[]>(() => {
    // Create lookup for discovered clusters by name
    const discoveredByName = keyBy(
      discovered.discoveredClusterRowModels,
      'name'
    );

    // Merge cast clusters with discovered data when isDiscovered = true
    const mergedCastRows = cast.clusterRowModels.map<ClusterRowModel>(
      (castRow) => {
        if (
          !castRow.isDiscovered &&
          castRow.displayStatus !== ClusterDisplayStatus.READ_ONLY
        )
          return castRow;

        const discoveredRow = discoveredByName[castRow.name];
        if (!discoveredRow) return castRow;

        // Merge CAST cluster + discovered cluster
        return {
          ...castRow,
          displayMetrics: {
            ...castRow.displayMetrics,
            ...discoveredRow.displayMetrics,
            resources: mergeResources(
              castRow.displayMetrics.resources,
              discoveredRow.displayMetrics.resources
            ),
          },
          summaryMetrics: {
            ...castRow.summaryMetrics,
            ...discoveredRow.summaryMetrics,
          },
          issuesDrawerMetrics: discoveredRow.issuesDrawerMetrics,
          error: castRow.error ?? discoveredRow.error,
        };
      }
    );

    // Get discovered clusters NOT in cast (by name)
    const castNames = new Set(cast.clusterRowModels.map((c) => c.name));
    const pureDiscovered = discovered.discoveredClusterRowModels.filter(
      (d) => !castNames.has(d.name)
    );

    return [...pureDiscovered, ...mergedCastRows];
  }, [discovered.discoveredClusterRowModels, cast.clusterRowModels]);

  const { activeClusters, notFoundClusters } = useMemo(
    () => partitionNotFoundClusters(clusterListRowModels),
    [clusterListRowModels]
  );

  return {
    data: activeClusters,
    notFoundClusters,
    isLoading,
    ...makeRefetchOnlyFailed(
      [cast.error, cast.refetch],
      [discovered.error, discovered.refetch]
    ),
  };
};
